import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';

import type { IntakeServiceId } from '../types';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatDoc = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--doc-rotation)); }
  50% { transform: translateY(-10px) rotate(var(--doc-rotation)); }
`;

const docs = [
  { width: 72, height: 92, left: 7, top: 10, rotation: '-14deg', opacity: 0.16 },
  { width: 56, height: 74, left: 74, top: 8, rotation: '9deg', opacity: 0.13 },
  { width: 88, height: 112, left: 12, top: 55, rotation: '-7deg', opacity: 0.11 },
  { width: 68, height: 88, left: 70, top: 52, rotation: '11deg', opacity: 0.15 },
  { width: 48, height: 62, left: 43, top: 5, rotation: '4deg', opacity: 0.1 },
  { width: 62, height: 80, left: 80, top: 73, rotation: '8deg', opacity: 0.11 },
];

interface IntakeStartServiceOption {
  id: IntakeServiceId;
  label: string;
  description: string;
}

interface IntakeStartViewProps {
  services?: IntakeStartServiceOption[];
  selectedServiceIds?: IntakeServiceId[];
  isSubmitting?: boolean;
  onToggleService?: (serviceId: IntakeServiceId) => void;
  onContinue?: () => void;
}

export function IntakeStartView({
  services = [],
  selectedServiceIds = [],
  isSubmitting = false,
  onToggleService,
  onContinue,
}: IntakeStartViewProps) {
  const theme = useTheme();
  const hasServicePicker = services.length > 0 && onToggleService;

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        px: 3,
        bgcolor: theme.palette.primary.dark,
        background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      }}
    >
      {docs.map((doc, index) => (
        <Box
          key={`${doc.left}-${doc.top}`}
          sx={{
            '--doc-rotation': doc.rotation,
            position: 'absolute',
            left: `${doc.left}%`,
            top: `${doc.top}%`,
            width: doc.width,
            height: doc.height,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.common.white, 0.055),
            border: `1px solid ${alpha(theme.palette.common.white, 0.095)}`,
            opacity: doc.opacity,
            transform: `rotate(${doc.rotation})`,
            animation: `${floatDoc} ${4.4 + index * 0.35}s ease-in-out ${index * 0.12}s infinite`,
          }}
        >
          <Box sx={{ mx: 1.5, mt: 1.5, height: 3, borderRadius: 1, bgcolor: alpha(theme.palette.common.white, 0.32) }} />
          <Box sx={{ mx: 1.5, mt: 0.75, height: 3, borderRadius: 1, bgcolor: alpha(theme.palette.common.white, 0.22), width: '62%' }} />
          <Box sx={{ mx: 1.5, mt: 0.75, height: 3, borderRadius: 1, bgcolor: alpha(theme.palette.common.white, 0.16), width: '78%' }} />
        </Box>
      ))}

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
          animation: `${fadeUp} 0.7s ease both`,
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ justifyContent: 'center', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.25,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.common.white, 0.12),
              color: theme.palette.common.white,
              boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, 0.22)}`,
            }}
          >
            <AutoAwesomeOutlinedIcon />
          </Box>
          <Typography sx={{ color: 'common.white', fontSize: 22, fontWeight: 850 }}>
            JotPsych Intake
          </Typography>
        </Stack>

        <Typography
          sx={{
            color: 'common.white',
            fontSize: { xs: 28, sm: 34 },
            lineHeight: 1.12,
            fontWeight: 850,
            mb: 2,
          }}
        >
          Uploading docs is a lot. We make it feel handled.
        </Typography>
        <Typography
          sx={{
            color: alpha(theme.palette.common.white, 0.68),
            fontSize: 15,
            lineHeight: 1.65,
          mb: hasServicePicker ? 2.5 : 4,
        }}
      >
        Add documents, credentials, and payer details once. Intake Hub carries them across
        JotCred, JotBill, and JotSite without duplicate asks.
      </Typography>

        {hasServicePicker && (
          <Stack spacing={1} sx={{ mb: 2.5 }}>
            {services.map(service => {
              const selected = selectedServiceIds.includes(service.id);
              return (
                <Box
                  key={service.id}
                  component="button"
                  type="button"
                  onClick={() => onToggleService(service.id)}
                  sx={{
                    appearance: 'none',
                    width: '100%',
                    border: `1px solid ${alpha(theme.palette.common.white, selected ? 0.42 : 0.14)}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.common.white, selected ? 0.16 : 0.075),
                    color: theme.palette.common.white,
                    cursor: 'pointer',
                    px: 1.5,
                    py: 1.25,
                    textAlign: 'left',
                    transition: 'background-color 0.14s ease, border-color 0.14s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.common.white, 0.18),
                    },
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 999,
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                        color: selected
                          ? theme.palette.common.white
                          : alpha(theme.palette.common.white, 0.48),
                      }}
                    >
                      <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 850 }}>
                        {service.label}
                      </Typography>
                      <Typography sx={{ color: alpha(theme.palette.common.white, 0.65), fontSize: 12 }}>
                        {service.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<UploadFileOutlinedIcon />}
          disabled={isSubmitting || (hasServicePicker && selectedServiceIds.length === 0)}
          onClick={onContinue}
          sx={{
            py: 1.35,
            borderRadius: 2,
            bgcolor: theme.palette.common.white,
            color: theme.palette.primary.dark,
            fontWeight: 850,
            boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.22)}`,
            '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.92) },
          }}
        >
          {isSubmitting ? 'Starting intake...' : 'Start intake'}
        </Button>
      </Box>
    </Box>
  );
}
